import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { WALL_DEFS, wallIdOfRegion } from "@gooner/shared";
import { usePublicSettings } from "../lib/publicSettings";
import {
  trackFapsUnseatedReclaimClicked,
  trackFapsUnseatedShown,
  useFapsStore,
} from "../lib/faps";
import { FapsAmount } from "../control/ui/FapsAmount";
import { IconClose } from "../control/ui/icons";

/**
 * The take-it-back card — the displaced side of bidding. When a square of yours is sniped,
 * the server pays you the cut and writes an `unseated` ledger row; the wallet's next poll
 * latches it into `unseatedPending` (there is no per-user push, so this arrives on the
 * next read — mount, tab focus, or the session's 5-minute refresh), and this card offers
 * the square straight back.
 *
 * "Take it back" is a normal takeover of whoever holds it now, so it just opens the wall's
 * buy dialog on that square (`/screen?code=0&buy=<regionId>`, read by DisplayApp) — no new
 * endpoint. A row without a regionId (one written before the region-carrying ref) still
 * says what happened; it simply has nothing to reopen.
 *
 * COPY names no money — the note is "@who took your <tier> square", and the cut is the
 * crown and a number (the Faps copy rules). Same card mechanics as the welcome and the cap
 * nudge: a portal fixed under the bar, dismiss-to-acknowledge (raising the localStorage
 * mark so it never returns for this event).
 */
export function FapsUnseatedCard() {
  const pending = useFapsStore((s) => s.unseatedPending);
  const dismiss = useFapsStore((s) => s.dismissUnseated);
  const settings = usePublicSettings();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    if (pending) trackFapsUnseatedShown();
  }, [pending]);

  if (!pending || !settings?.faps?.enabled) return null;
  const onTheWall = pathname === "/screen" || pathname === "/";

  return createPortal(
    <div
      role="status"
      className="fixed bottom-20 right-3 z-[55] w-[min(22rem,calc(100vw-1.5rem))] rounded-xl border border-[#f5c518]/40 bg-[var(--color-panel)] p-4 shadow-2xl"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-2 top-2 rounded p-1 text-zinc-500 hover:bg-white/10 hover:text-white"
      >
        <IconClose width={14} height={14} />
      </button>
      <p className="pr-6 text-sm text-zinc-100">{pending.note ?? "Your square was taken."}</p>
      {pending.cut > 0 ? (
        <p className="mt-1 text-xs text-zinc-400">
          Here&rsquo;s <FapsAmount n={pending.cut} size={11} /> back.
        </p>
      ) : null}
      <div className="mt-3 flex gap-2">
        {pending.regionId && !onTheWall ? (
          <button
            type="button"
            onClick={() => {
              trackFapsUnseatedReclaimClicked();
              const region = pending.regionId ?? "";
              // The square names its wall; open THAT board (the Premium Wall needs naming,
              // a bare /screen plays the Community Wall) on the square via `?buy`. A signed-in
              // /screen becomes a device and redirects — PendingScreen carries the `?buy`
              // through that redirect, and the device's board reads it.
              const wallId = wallIdOfRegion(region);
              const view = wallId ? WALL_DEFS[wallId].viewId : null;
              dismiss();
              navigate(
                `/screen?code=0&buy=${encodeURIComponent(region)}${view ? `&view=${encodeURIComponent(view)}` : ""}`,
              );
            }}
            className="rounded-lg bg-[#f5c518] px-3 py-1.5 text-sm font-semibold text-black hover:brightness-110"
          >
            Take it back
          </button>
        ) : null}
        <button
          type="button"
          onClick={dismiss}
          className="rounded-lg border border-[var(--color-edge)] px-3 py-1.5 text-sm font-medium text-zinc-200 hover:bg-white/10"
        >
          Let it go
        </button>
      </div>
    </div>,
    document.body,
  );
}
